import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Autocomplete,
  Box
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { salesService, creditNoteService } from '../../services';
import { useTranslation } from '../../hooks/useTranslation';

const CreditNoteDetails = ({
  creditNote,
  salesInvoices,
  selectedInvoice,
  controlStyle,
  onCreditNoteChange,
  onDateChange,
  onInvoiceSelect
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  
  useEffect(() => {
    if (!creditNote.creditNoteNumber) {
      fetchNextCreditNoteNumber();
    }
  }, []);

  useEffect(() => {
    if (selectedInvoice && selectedInvoice.invoiceNo) {
      const updatedCreditNote = {
        ...creditNote,
        originalInvoice: selectedInvoice.invoiceNo
      };
      console.log('CreditNoteDetails: Updating originalInvoice field:', selectedInvoice.invoiceNo);
      
      onCreditNoteChange(updatedCreditNote);
    }
  }, [selectedInvoice]);

  const processInvoiceData = (invoiceData) => {
    try {
      const { master, details } = invoiceData;
      
      if (master && details) {
        
        const completeInvoice = {
          invoiceNo: master.invoiceNo,
          invoiceDate: master.invoiceDate,
          customerName: master.customerName,
          customerCode: master.customerCode,
          vatNumber: master.vat,
          customerAddress: master.customerAddress,
          customerPhone: master.customerPhone,
          items: details.map(item => ({
            itemCode: item.itemCode,
            description: item.description,
            quantity: item.qty,
            price: item.unitPrice,
            discount: item.discountPerc,
            vatRate: item.vat,
            total: item.grandTotal
          }))
        };
        
        
        onInvoiceSelect(null, completeInvoice);
        
        const updatedCreditNote = {
          ...creditNote,
          customer: {
            name: master.customerName,
            code: master.customerCode,
            vatNumber: master.vat,
            streetName: master.customerAddress,
            phone: master.customerPhone
          }
        };
        onCreditNoteChange(updatedCreditNote);
      } else {
        console.warn('Missing master or details data in invoice');
      }
    } catch (error) {
      console.error('Error processing invoice data:', error);
    }
  };

  const fetchInvoiceDetails = async (invoiceNumber) => {
    try {
      
      const response = await salesService.getSalesInvoiceByInvoiceNo(invoiceNumber);

      if (response && response.success) {
        const { master, details } = response.data;
        
        const completeInvoice = {
          ...selectedInvoice,
          invoiceNo: master.invoiceNo,
          invoiceDate: master.invoiceDate,
          customerName: master.customerName,
          customerCode: master.customerCode,
          vatNumber: master.vat,
          customerAddress: master.customerAddress,
          customerPhone: master.customerPhone,
          items: details.map(item => ({
            itemCode: item.itemCode,
            description: item.description,
            quantity: item.qty,
            price: item.unitPrice,
            discount: item.discountPerc,
            vatRate: item.vat,
            total: item.grandTotal
          }))
        };
        
        onInvoiceSelect(null, completeInvoice);
        
        const updatedCreditNote = {
          ...creditNote,
          customer: {
            name: master.customerName,
            code: master.customerCode,
            vatNumber: master.vat,
            streetName: master.customerAddress,
            phone: master.customerPhone
          }
        };
        onCreditNoteChange(updatedCreditNote);
      } else {
        console.warn('API request failed or returned no data:', response.data);
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
    }
  };

  const fetchNextCreditNoteNumber = async () => {
    setIsLoading(true);
    try {
      const response = await creditNoteService.getNextCreditNoteNumber();

      if (response && response.success) {
        console.log('Fetched next credit note number:', response.nextInvoiceNumber);
        console.log('Current creditNote state before update:', creditNote);
        
        onCreditNoteChange(prev => ({
          ...prev,
          creditNoteNumber: response.nextInvoiceNumber
        }));
      }
    } catch (error) {
      console.error('Error fetching next credit note number:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleReasonChange = (e) => {
    const newReason = e.target.value;
    onCreditNoteChange(prev => ({
      ...prev,
      reasonForCreditNote: newReason
    }));
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 0.5, 
        mb: 0.5, 
        borderRadius: '4px',
        boxShadow: 'none',
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, fontSize: '0.875rem' }}>
        {t("CreditNotes.Credit_Note_Details")}
      </Typography>
      <Grid container spacing={0.5}>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t('Common.Ref_Invoice_No')}
            value={selectedInvoice ? selectedInvoice.invoiceNo || selectedInvoice.number || '' : ''}
            InputProps={{
              readOnly: true,
            }}
            required
            fullWidth
            margin="dense"
            size="small"
            sx={controlStyle}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t('CreditNotes.Credit_Note_Number')}
            value={isLoading ? 'Loading...' : (creditNote.creditNoteNumber || '')}
            fullWidth
            margin="dense"
            size="small"
            InputProps={{
              readOnly: true,
            }}
            sx={controlStyle}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t('CreditNotes.Ref_Invoice_Date')}
              value={selectedInvoice ? 
                (selectedInvoice.invoiceDate ? new Date(selectedInvoice.invoiceDate) : 
                 selectedInvoice.date ? new Date(selectedInvoice.date) : null) : 
                null
              }
              readOnly
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  margin: "dense",
                  size: "small",
                  sx: controlStyle,
                  InputProps: { readOnly: true }
                } 
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t('Common.Date')}
              value={creditNote.date}
              readOnly
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  margin: "dense",
                  size: "small",
                  sx: controlStyle,
                  InputProps: { readOnly: true }
                } 
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t('CreditNotes.Reason_for_Credit_Note')}
            value={creditNote.reasonForCreditNote || ''}
            onChange={handleReasonChange}
            fullWidth
            multiline
            rows={2.5}
            margin="dense"
            size="small"
            required
            error={!creditNote.reasonForCreditNote}
            helperText={!creditNote.reasonForCreditNote ? t('CreditNotes.Reason_is_required') : ''}
            sx={{
              ...controlStyle,
              '& .MuiOutlinedInput-root': {
                minHeight: 90,
                borderRadius: 0
              }
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CreditNoteDetails;
